import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Typography from '@mui/material/Typography';
import { getTrans } from 'modules/i18n';
import { AvailabilityDay, Weekday } from '../types';
import { formatRange, weekdayLabel } from '../utils';
import { RangeListEditor } from './RangeListEditor';

const daySummary = (day: AvailabilityDay): string =>
  day.available.length
    ? day.available.map(formatRange).join(', ')
    : getTrans('availabilityPage.day.unavailable');

interface Props {
  days: AvailabilityDay[];
  onChange: (days: AvailabilityDay[]) => void;
  dataTestId?: string;
}

/**
 * Full-week (Mon–Sun) pattern editor: one accordion per day with available and
 * preferred range lists. A day without available ranges is "unavailable" — the
 * API's sparse-day representation.
 */
export const DayPatternEditor = ({
  days,
  onChange,
  dataTestId = 'day-pattern-editor'
}: Props) => {
  const updateDay = (weekday: Weekday, patch: Partial<AvailabilityDay>) =>
    onChange(
      days.map(day => (day.dayOfWeek === weekday ? { ...day, ...patch } : day))
    );

  return (
    <div data-testid={dataTestId}>
      {days.map(day => (
        <Accordion key={day.dayOfWeek} disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={{ fontWeight: 600, minWidth: 96 }}>
              {weekdayLabel(day.dayOfWeek)}
            </Typography>
            <Typography
              variant="body2"
              color={day.available.length ? 'text.secondary' : 'text.disabled'}
              sx={{ alignSelf: 'center' }}
            >
              {daySummary(day)}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <RangeListEditor
              label={getTrans('availabilityPage.editor.availableLabel')}
              ranges={day.available}
              onChange={available => updateDay(day.dayOfWeek, { available })}
              addLabel={getTrans('availabilityPage.editor.addHours')}
              dataTestId={`${dataTestId}-${day.dayOfWeek}-available`}
            />
            <RangeListEditor
              label={getTrans('availabilityPage.editor.preferredLabel')}
              ranges={day.preferred}
              onChange={preferred => updateDay(day.dayOfWeek, { preferred })}
              addLabel={getTrans('availabilityPage.editor.addPreferred')}
              dataTestId={`${dataTestId}-${day.dayOfWeek}-preferred`}
            />
          </AccordionDetails>
        </Accordion>
      ))}
    </div>
  );
};
